import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Key as KeyIcon, Desktop, Wrench, Printer, IdentificationCard, BookOpen, Toolbox } from '@phosphor-icons/react';

/**
 * Article selector with grouped sections + visible separators.
 * Sections: Clés, Produits IT, Impression, Cartes de visite, Reliure, Prestations de service, Interventions à domicile.
 *
 * Props:
 *   value: current "type:id" value
 *   onValueChange: callback
 *   keys, itProducts, services, printing, businessCards, binding, prestations: arrays from backend
 *   testId: optional data-testid for the trigger
 */
const ArticleSelector = ({
  value,
  onValueChange,
  keys = [],
  itProducts = [],
  services = [],
  printing = [],
  businessCards = [],
  binding = [],
  prestations = [],
  testId = 'article-select',
  // When true, zero-stock products stay selectable (used by Devis/Quotes — you can quote
  // a product you don't have in stock yet). Defaults to false → invoices keep blocking.
  allowZeroStock = false,
}) => {
  const servicesByCategory = services.reduce((acc, s) => {
    const cat = s.category || 'Général';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const stockSections = [
    {
      label: 'Clés',
      icon: KeyIcon,
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      items: keys,
      renderItem: (k) => ({
        key: `key:${k.id}`,
        value: `key:${k.id}`,
        disabled: !allowZeroStock && k.stock_quantity === 0,
        text: `${k.name}${k.characteristics ? ' - ' + k.characteristics : ''} — ${k.sale_price.toFixed(2)} € (Stock: ${k.stock_quantity})`,
      }),
    },
    {
      label: 'Produits IT',
      icon: Desktop,
      color: 'text-blue-700',
      bg: 'bg-blue-50',
      items: itProducts,
      renderItem: (p) => ({
        key: `it_product:${p.id}`,
        value: `it_product:${p.id}`,
        disabled: !allowZeroStock && p.stock_quantity === 0,
        text: `${p.name}${p.category ? ' (' + p.category + ')' : ''} — ${p.sale_price.toFixed(2)} € (Stock: ${p.stock_quantity})`,
      }),
    },
  ];

  const printingSections = [
    {
      label: 'Impression',
      icon: Printer,
      color: 'text-indigo-700',
      bg: 'bg-indigo-50',
      items: printing,
      renderItem: (p) => ({
        key: `printing:${p.id}`,
        value: `printing:${p.id}`,
        disabled: false,
        text: `${p.format} - ${p.paper_type} - ${p.is_color ? 'Couleur' : 'Monochrome'} — ${p.price.toFixed(2)} €/page`,
      }),
    },
    {
      label: 'Cartes de visite',
      icon: IdentificationCard,
      color: 'text-purple-700',
      bg: 'bg-purple-50',
      items: businessCards,
      renderItem: (b) => ({
        key: `business_card:${b.id}`,
        value: `business_card:${b.id}`,
        disabled: false,
        text: `Papier ${b.paper_type} - ${b.is_color ? 'Couleur' : 'Monochrome'} — ${b.price.toFixed(2)} €/carte`,
      }),
    },
    {
      label: 'Reliure',
      icon: BookOpen,
      color: 'text-pink-700',
      bg: 'bg-pink-50',
      items: binding,
      renderItem: (b) => ({
        key: `binding:${b.id}`,
        value: `binding:${b.id}`,
        disabled: false,
        text: `${b.min_pages} à ${b.max_pages} pages — ${b.price.toFixed(2)} € forfait`,
      }),
    },
    {
      label: 'Prestations de service',
      icon: Toolbox,
      color: 'text-teal-700',
      bg: 'bg-teal-50',
      items: prestations,
      renderItem: (p) => ({
        key: `prestation:${p.id}`,
        value: `prestation:${p.id}`,
        disabled: false,
        text: `${p.name} — ${Number(p.price).toFixed(2)} € TTC`,
      }),
    },
  ];

  const allSections = [...stockSections, ...printingSections];
  const hasAnyContent = allSections.some((s) => s.items.length > 0) || services.length > 0;

  let renderedCount = 0;

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder="Sélectionner un article…" />
      </SelectTrigger>
      <SelectContent className="max-h-96">
        {!hasAnyContent && (
          <div className="px-3 py-4 text-sm text-slate-500 text-center">Aucun article disponible</div>
        )}

        {allSections.map((sec) => {
          if (sec.items.length === 0) return null;
          const Icon = sec.icon;
          const shouldRenderSeparator = renderedCount > 0;
          renderedCount += 1;
          return (
            <React.Fragment key={sec.label}>
              {shouldRenderSeparator && <div className="my-1 border-t border-slate-200" />}
              <div className={`flex items-center gap-2 px-3 py-2 ${sec.bg} sticky top-0 z-10`}>
                <Icon size={14} weight="bold" className={sec.color} />
                <span className={`text-xs font-bold uppercase tracking-wider ${sec.color}`}>
                  {sec.label} ({sec.items.length})
                </span>
              </div>
              {sec.items.map((it) => {
                const r = sec.renderItem(it);
                return (
                  <SelectItem key={r.key} value={r.value} disabled={r.disabled}>
                    <span className="text-sm">{r.text}</span>
                  </SelectItem>
                );
              })}
            </React.Fragment>
          );
        })}

        {services.length > 0 && (
          <>
            {renderedCount > 0 && <div className="my-1 border-t border-slate-200" />}
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 sticky top-0 z-10">
              <Wrench size={14} weight="bold" className="text-emerald-700" />
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                Interventions à domicile ({services.length})
              </span>
            </div>
            {Object.entries(servicesByCategory).map(([cat, items], catIdx) => (
              <React.Fragment key={`svc-cat-${cat}`}>
                {catIdx > 0 && <div className="mx-3 my-0.5 border-t border-dashed border-slate-200" />}
                {Object.keys(servicesByCategory).length > 1 && (
                  <div className="px-5 py-1 text-xs italic text-slate-500">{cat}</div>
                )}
                {items.map((s) => (
                  <SelectItem key={`service:${s.id}`} value={`service:${s.id}`}>
                    <span className="text-sm">
                      {s.name} — {s.unit_price.toFixed(2)} €/{s.unit_label}
                    </span>
                  </SelectItem>
                ))}
              </React.Fragment>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
};

export default ArticleSelector;
